import React, { ReactNode, RefObject } from 'react';
import { SelectState } from 'react-stately/useSelectState';
import { CollectionChildren } from '@react-types/shared';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
} from './useSapphireStyleProps';
import { GlobalDomAttributes } from './types';
import { StatelessListBox } from '../ListBox/src/StatelessListBox';
import { MultiSelectState } from '../Select/src/useMultiSelectState';
import { ComboBoxState } from 'react-stately/useComboBoxState';
import { Popover } from '../Popover/src/Popover';

export interface ListBoxPopoverProps<T>
  extends GlobalDomAttributes,
    SapphireStyleProps {
  state: SelectState<T> | MultiSelectState<T> | ComboBoxState<T>;
  listBoxProps: any;
  children: CollectionChildren<T>;
  selectWidth?: number;
  popoverRef: RefObject<HTMLDivElement>;
  triggerRef: RefObject<HTMLElement>;
  listBoxRef: RefObject<HTMLUListElement>;
  loadingState?: 'loading';
  loadingSkeletonRowsCount?: number;
  disableSelectedStyles?: boolean;
  emptyState?: ReactNode;
  isNonModal?: boolean;
  /**
   * @default "lg"
   */
  size?: 'lg' | 'md' | 'sm';
  /**
   * A ref to an input element that should be used for keyboard navigation
   * instead of the listbox itself.
   */
  connectedInputRef?: RefObject<HTMLInputElement>;
}

export const ListBoxPopover = <T extends object>(
  props: ListBoxPopoverProps<T>
): JSX.Element | null => {
  const {
    isNonModal,
    state,
    listBoxProps,
    children,
    selectWidth,
    triggerRef,
    popoverRef,
    loadingState,
    loadingSkeletonRowsCount,
    emptyState,
    disableSelectedStyles,
    size,
    connectedInputRef,
  } = props;

  const { styleProps } = useSapphireStyleProps(props);

  return (
    <Popover
      ref={popoverRef}
      state={state}
      triggerRef={triggerRef}
      placement="bottom start"
      isNonModal={isNonModal}
      UNSAFE_style={{ minWidth: selectWidth || 1 }}
      UNSAFE_className={styleProps.className}
    >
      <StatelessListBox
        {...listBoxProps}
        height="100%"
        size={size}
        ref={props.listBoxRef}
        state={state}
        disallowEmptySelection
        loadingState={loadingState}
        loadingSkeletonRowsCount={loadingSkeletonRowsCount}
        emptyState={emptyState}
        disableSelectedStyles={disableSelectedStyles}
        connectedInputRef={connectedInputRef}
      >
        {children}
      </StatelessListBox>
    </Popover>
  );
};
